import dayjs, { Dayjs } from 'dayjs';
import range from 'lodash-es/range';
import { PartialDate } from '@tcl-argon-models/models';
import { take } from './generator';

enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

// Used for ordering of days of week in calendar.
export const DAY_OF_WEEK_ORDERING = [
  DayOfWeek.Sunday,
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
];

export const DAY_OF_WEEK_TO_INFO_MAPPING = {
  [DayOfWeek.Sunday]: {
    abbreviation: 'S',
  },
  [DayOfWeek.Monday]: {
    abbreviation: 'M',
  },
  [DayOfWeek.Tuesday]: {
    abbreviation: 'T',
  },
  [DayOfWeek.Wednesday]: {
    abbreviation: 'W',
  },
  [DayOfWeek.Thursday]: {
    abbreviation: 'T',
  },
  [DayOfWeek.Friday]: {
    abbreviation: 'F',
  },
  [DayOfWeek.Saturday]: {
    abbreviation: 'S',
  },
};

function getWeekdayMonthEndsOn(dateTime: Dayjs): number {
  return dateTime.date(dateTime.daysInMonth()).day();
}

// Weeks start on Sunday (0) and go until Saturday(6)
function countOfDaysBeforeWeekday(weekday: number): number {
  if (weekday < 0 || weekday > 6) {
    throw new Error('Bad weekday number. Weekdays range from 0 to 6');
  }

  return DAY_OF_WEEK_ORDERING[weekday];
}

// Weeks start on Sunday (0) and go until Saturday(6)
function countOfDaysAfterWeekday(weekday: number): number {
  if (weekday < 0 || weekday > 6) {
    throw new Error('Bad weekday number. Weekdays range from 0 to 6');
  }

  return 6 - DAY_OF_WEEK_ORDERING[weekday];
}

function* monthDayGenerator(direction: 'forwards' | 'backwards', monthLength: number) {
  if (monthLength < 28 || monthLength > 31) {
    throw new Error('Bad month length');
  }

  for (let index = 0; index < monthLength; index++) {
    if (direction === 'forwards') {
      yield index + 1;
    } else {
      yield monthLength - index;
    }
  }
}

function getDaysPrecedingMonth(dateTime: Dayjs): number[] {
  if (dateTime.date() !== 1) {
    console.log(dateTime);
    throw new Error('getDaysPrecedingMonth expects a dateTime with day set to 1');
  }

  const leftoverWeekdays = countOfDaysBeforeWeekday(dateTime.day());
  const daysInMonth = dateTime.subtract(1, 'month').daysInMonth();
  const mdg = monthDayGenerator('backwards', daysInMonth);
  const precedingDays: number[] = take(mdg, leftoverWeekdays);

  return precedingDays.sort();
}

function getDaysFollowingMonth(dateTime: Dayjs): number[] {
  if (dateTime.date() !== 1) {
    console.log(dateTime);
    throw new Error('getDaysFollowingMonth expects a dateTime with day set to 1');
  }

  const weekday = getWeekdayMonthEndsOn(dateTime);
  const leftoverWeekdays = countOfDaysAfterWeekday(weekday);
  const daysInMonth = dateTime.add(1, 'month').daysInMonth();
  const mdg = monthDayGenerator('forwards', daysInMonth);
  const followingDays: number[] = take(mdg, leftoverWeekdays);

  return followingDays;
}

export const DATE_FORMAT = 'MM/DD/YYYY';

function toISO(dateString: string) {
  const [month, day, year] = dateString.split('/');

  return `${year}-${month}-${day}`;
}

export function dateStringToDayjs(dateString: string) {
  return dayjs(toISO(dateString));
}

export function incrementDateStringDay(dateString: string) {
  return dayjs(toISO(dateString)).add(1, 'day').format(DATE_FORMAT);
}

export function decrementDateStringDay(dateString: string) {
  return dayjs(toISO(dateString)).subtract(1, 'day').format(DATE_FORMAT);
}

export function incrementDateStringWeek(dateString: string) {
  return dayjs(toISO(dateString)).add(1, 'week').format(DATE_FORMAT);
}

export function decrementDateStringWeek(dateString: string) {
  return dayjs(toISO(dateString)).subtract(1, 'week').format(DATE_FORMAT);
}

export function todayDateString() {
  return dayjs().format(DATE_FORMAT);
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const formatMonthYear = (month: number, year: number): string => {
  if (month < 1 || month > 12) {
    throw new Error(`formatDate expects a month-number between 1 and 12, but you passed ${month}. Please fix this.`);
  }

  return `${months[month - 1]} ${year}`;
};

/**
 * A utility for generating days displayed by a calendar for a given month and year.
 * @param month A number representing a month. Starts with 1 (January) and goes to 12 (December).
 * @param year A four-digit year.
 * Calendar sizing is dynamic. Different months start on different weekdays, thereby requiring different amounts of padding.
 * This function provides all info necessary to generate a calendar where each row has at least one day in the given month and
 * no rows that contain less than 7 days.
 */
export function getCalendarData(month: number, year: number) {
  if (month < 1 || month > 12) {
    throw new Error(`formatDate expects a month-number between 1 and 12, but you passed ${month}. Please fix this.`);
  }

  const firstDateOfMonth = dayjs()
    .year(year)
    .month(month - 1)
    .date(1);

  const currentPartialDate = {
    day: firstDateOfMonth.date(),
    // dayjs months are zero-indexed
    month: firstDateOfMonth.month() + 1,
    year: firstDateOfMonth.year(),
  };
  const previousDate = firstDateOfMonth.subtract(1, 'month');
  const previousPartialDate = {
    // dayjs months are zero-indexed
    month: previousDate.month() + 1,
    year: previousDate.year(),
  };
  const followingDate = firstDateOfMonth.add(1, 'month');
  const followingPartialDate = {
    // dayjs months are zero-indexed
    month: followingDate.month() + 1,
    year: followingDate.year(),
  };

  const daysPrecedingSelectedMonth = getDaysPrecedingMonth(firstDateOfMonth);
  // lodash/range is non-inclusive
  const daysInSelectedMonth = range(1, firstDateOfMonth.daysInMonth() + 1);
  const daysFollowingSelectedMonth = getDaysFollowingMonth(firstDateOfMonth);

  return {
    previousPartialDate,
    daysPrecedingSelectedMonth,
    // TODO: NJM shoudl probably be called currentDate since it has a day
    currentPartialDate,
    daysInSelectedMonth,
    // TODO: NJM better name might be followingMonthMonthYear
    followingPartialDate,
    daysFollowingSelectedMonth,
  };
}

/**
 * Increment a month and year in naive fashion. Expects a 0-indexed month.
 * @param month One-indexed month.
 * @param year a number representing the year.
 */
export const incrementMonthYear = (month: number, year: number): { month: number; year: number } => {
  const nextMonth = month + 1;

  if (nextMonth > 12) {
    return { month: 1, year: year + 1 };
  } else {
    return { month: nextMonth, year };
  }
};

export const getToday = (): PartialDate => {
  const today = dayjs();
  return {
    day: today.date(),
    month: today.month() + 1,
    year: today.year(),
  };
};

export const arePartialDatesEqual = (firstDate: PartialDate, secondDate: PartialDate): boolean => {
  return firstDate.day === secondDate.day && firstDate.month === secondDate.month && firstDate.year === secondDate.year;
};

export const convertDateObjectToNativeDate = (date: PartialDate): Date => {
  return new Date(date.year, date.month - 1, date.day);
};

export enum DayInWeekPlacement {
  BEGINNING_OF_WEEK = 'BEGINNING_OF_WEEK',
  MIDDLE_OF_WEEK = 'MIDDLE_OF_WEEK',
  END_OF_WEEK = 'END_OF_WEEK',
  OUTSIDE_OF_WEEK = 'OUTSIDE_OF_WEEK',
}

export enum DayInMonthPlacement {
  BEGINNING_OF_MONTH = 'BEGINNING_OF_MONTH',
  MIDDLE_OF_MONTH = 'MIDDLE_OF_MONTH',
  END_OF_MONTH = 'END_OF_MONTH',
  OUTSIDE_OF_MONTH = 'OUTSIDE_OF_MONTH',
}

export const getDayInMonthPlacement = (date: PartialDate): DayInMonthPlacement => {
  if (date.day === 1) {
    return DayInMonthPlacement.BEGINNING_OF_MONTH;
  }

  if (
    dayjs()
      .year(date.year)
      .month(date.month - 1)
      .daysInMonth() === date.day
  ) {
    return DayInMonthPlacement.END_OF_MONTH;
  }

  return DayInMonthPlacement.MIDDLE_OF_MONTH;
};

export const getDayInWeekPlacement = (date: PartialDate): DayInWeekPlacement => {
  const dayOfWeek = dayjs()
    .year(date.year)
    .month(date.month - 1)
    .date(date.day)
    .day();

  if (dayOfWeek === DAY_OF_WEEK_ORDERING[0]) {
    return DayInWeekPlacement.BEGINNING_OF_WEEK;
  } else if (dayOfWeek === DAY_OF_WEEK_ORDERING[DAY_OF_WEEK_ORDERING.length - 1]) {
    return DayInWeekPlacement.END_OF_WEEK;
  }

  return DayInWeekPlacement.MIDDLE_OF_WEEK;
};

export const isDateInSelectableRange = (date: string, startDate: string, endDate: string): boolean => {
  return (
    (dayjs(date).isSame(startDate) || dayjs(date).isAfter(startDate)) &&
    (dayjs(date).isSame(endDate) || dayjs(date).isBefore(endDate))
  );
};

export const isDateInExcludedDates = (
  date: string,
  excludedDates: Array<{ startDateString: string; endDateString: string }>,
): boolean => {
  let isDateExcluded = false;
  excludedDates.forEach((dateRange) => {
    if (isDateInSelectableRange(date, dateRange.startDateString, dateRange.endDateString)) {
      isDateExcluded = true;
    }
  });
  return isDateExcluded;
};
